import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { normalizePermissions } from "@/lib/permissions";
import { useAuth } from "@/contexts/AuthContext";
import PermissionsMatrix from "@/components/PermissionsMatrix";
import { Loader2, ShieldCheck, KeyRound, Mail, Save, Crown } from "lucide-react";

const BRAND_ROLES = [
  { v: "owner", l: "Owner — full access incl. billing" },
  { v: "billing", l: "Billing — pricing & invoices" },
  { v: "operations", l: "Operations — day-to-day" },
  { v: "viewer", l: "Viewer — read-only" },
];

export default function UserAccessDialog({ user, mode = "admin", onClose, onSaved }) {
  const { isSuperAdmin } = useAuth();
  const isBoxHeroAdmin = user.role === "admin";
  const isStaff = user.role === "admin" || user.role === "returns_manager";
  const [role, setRole] = useState(user.role || "viewer");
  const [perms, setPerms] = useState(normalizePermissions(user.permissions));
  const [superAdmin, setSuperAdmin] = useState(!!user.super_admin);
  const [saving, setSaving] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [resetSending, setResetSending] = useState(false);

  const base = mode === "team" ? `/team/users/${user.id}` : `/admin/users/${user.id}`;
  // Only a super admin can edit another employee's module access / super-admin flag.
  const canEditStaffAccess = isSuperAdmin;

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (isBoxHeroAdmin) {
        body.super_admin = superAdmin;
        if (!superAdmin) body.permissions = perms;
      } else if (isStaff) {
        body.permissions = perms;
      } else {
        body.role = role;
        body.permissions = perms;
      }
      await api.patch(base, body);
      toast.success("Access updated");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setPassword = async () => {
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setPwSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/set-password`, { password: newPw });
      toast.success(`Password set for ${user.email}`);
      setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPwSaving(false);
    }
  };

  const sendReset = async () => {
    setResetSending(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/send-reset`);
      if (data.ok) toast.success(`Reset link emailed to ${user.email}`);
      else toast.warning(`Couldn't send email${data.email_error ? `: ${data.email_error}` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="user-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600" /> Access — {user.name || user.email}</DialogTitle>
          <DialogDescription>Choose what this person can view and manage.{mode === "admin" ? " You can also reset their password." : ""}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <p className="text-sm text-slate-500">{user.email}</p>

          {!isStaff && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="access-role-select"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                {BRAND_ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </div>
          )}

          {isBoxHeroAdmin ? (
            <div className="space-y-4">
              <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${superAdmin ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
                <Crown className={`w-5 h-5 mt-0.5 shrink-0 ${superAdmin ? "text-amber-500" : "text-slate-400"}`} />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">Super admin</p>
                  <p className="text-xs text-slate-500 mt-0.5">Full access to everything, including billing and pricing. Can manage other employees and their access.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="access-superadmin-toggle"
                    checked={superAdmin}
                    disabled={!canEditStaffAccess}
                    onChange={(e) => setSuperAdmin(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-7 bg-slate-300 peer-checked:bg-amber-500 rounded-full peer-disabled:opacity-50 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:after:translate-x-5" />
                </label>
              </div>

              {!superAdmin && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">What can this employee access?</label>
                  <PermissionsMatrix value={perms} onChange={setPerms} disabled={!canEditStaffAccess} />
                  <p className="text-xs text-slate-400 mt-2">Toggle any module on or off. <strong>Billing &amp; Pricing</strong> stays off unless you switch it on here.</p>
                </div>
              )}
              {!canEditStaffAccess && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Only a super admin (e.g. Sergey) can change employee access.</p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">What can they access?</label>
              <PermissionsMatrix value={perms} onChange={setPerms} />
              <p className="text-xs text-slate-400 mt-2">Tip: leave <strong>Billing &amp; Pricing</strong> unchecked for employees who shouldn't see costs.</p>
            </div>
          )}

          <button onClick={save} disabled={saving || (isBoxHeroAdmin && !canEditStaffAccess)} data-testid="access-save-btn"
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg inline-flex items-center justify-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save access
          </button>

          {mode === "admin" && (
          <div className="border-t border-slate-100 pt-4">
            <p className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><KeyRound className="w-4 h-4" /> Password</p>
            <div className="flex gap-2 mb-2">
              <input type="password" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Set a new password…"
                data-testid="access-newpw-input"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
              <button onClick={setPassword} disabled={pwSaving} data-testid="access-setpw-btn"
                className="bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white text-sm font-semibold px-4 rounded-md">
                {pwSaving ? "Saving…" : "Set"}
              </button>
            </div>
            <button onClick={sendReset} disabled={resetSending} data-testid="access-sendreset-btn"
              className="text-sm text-blue-600 hover:underline font-medium inline-flex items-center gap-1.5">
              <Mail className="w-4 h-4" /> {resetSending ? "Sending…" : "Email a reset link instead"}
            </button>
          </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
